import { useState, useRef, useEffect, useLayoutEffect, useCallback, isValidElement, cloneElement, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DropdownItem {
  /** Unique value passed to onSelect */
  value: string;
  /** Display label */
  label: string;
  /** Optional icon rendered before the label */
  icon?: React.ReactNode;
  /** Disable this item */
  disabled?: boolean;
  /** Render in destructive (red) style */
  destructive?: boolean;
}

export interface DropdownDivider {
  type: 'divider';
}

export type DropdownEntry = DropdownItem | DropdownDivider;

interface DropdownProps {
  /** The trigger element (button, icon, etc.) */
  trigger: React.ReactNode;
  /** Menu entries (items + dividers) */
  items: DropdownEntry[];
  /** Callback when an item is selected */
  onSelect: (value: string) => void;
  /** Menu alignment relative to trigger */
  align?: 'left' | 'right';
  className?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface MenuPosition {
  top: number;
  left?: number;
  right?: number;
}

function samePosition(a: MenuPosition, b: MenuPosition): boolean {
  return a.top === b.top && a.left === b.left && a.right === b.right;
}

/** True when the rect lies entirely outside the viewport (the anchor scrolled away). */
function isRectOutsideViewport(rect: DOMRect): boolean {
  return (
    rect.bottom < 0 ||
    rect.top > window.innerHeight ||
    rect.right < 0 ||
    rect.left > window.innerWidth
  );
}

function isDivider(entry: DropdownEntry): entry is DropdownDivider {
  return 'type' in entry && entry.type === 'divider';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Dropdown({
  trigger,
  items,
  onSelect,
  align = 'left',
  className,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(-1);
  const [menuPos, setMenuPos] = useState<MenuPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const actionableItems = items.filter(
    (it): it is DropdownItem => !isDivider(it) && !it.disabled,
  );

  const close = useCallback(() => {
    setIsOpen(false);
    setFocusIdx(-1);
  }, []);

  // --------------------------------------------------
  // Position menu (portal-mounted, fixed positioning)
  // --------------------------------------------------
  const computePosition = useCallback((): MenuPosition | null => {
    if (!containerRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    const top = rect.bottom + 4;
    return align === 'right'
      ? { top, right: window.innerWidth - rect.right }
      : { top, left: rect.left };
  }, [align]);

  useLayoutEffect(() => {
    setMenuPos(isOpen ? computePosition() : null);
  }, [isOpen, computePosition]);

  // While open the menu FOLLOWS its anchor on scroll/resize instead of closing:
  // the scroll that brings the trigger into view (scrollIntoView, keyboard focus,
  // inertial trackpad scroll, mobile) fires AFTER the click opened the menu, and
  // closing on it swallowed the first click (FEAT-034 TASK-047, N-04). The menu
  // closes only once the trigger leaves the viewport; scrolling inside the menu
  // itself is ignored.
  useEffect(() => {
    if (!isOpen) return;
    const reposition = (e: Event) => {
      if (e.target instanceof Node && menuRef.current?.contains(e.target)) return;
      if (!containerRef.current) return;
      if (isRectOutsideViewport(containerRef.current.getBoundingClientRect())) {
        close();
        return;
      }
      const next = computePosition();
      setMenuPos((prev) => (next && prev && samePosition(prev, next) ? prev : next));
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [isOpen, close, computePosition]);

  // --------------------------------------------------
  // Close on outside click (now needs to check menuRef too since portal
  // renders outside the container's DOM subtree)
  // --------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = containerRef.current?.contains(target);
      const inMenu = menuRef.current?.contains(target);
      if (!inTrigger && !inMenu) {
        close();
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, close]);

  // --------------------------------------------------
  // Close on ESC
  // --------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  // --------------------------------------------------
  // Focus active menu item on focusIdx change
  // --------------------------------------------------
  useEffect(() => {
    if (focusIdx < 0 || !menuRef.current) return;
    const buttons = menuRef.current.querySelectorAll<HTMLButtonElement>(
      '[role="menuitem"]:not([disabled])',
    );
    buttons[focusIdx]?.focus();
  }, [focusIdx]);

  // --------------------------------------------------
  // Keyboard navigation
  // --------------------------------------------------
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
        setFocusIdx(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusIdx((prev) => (prev + 1) % actionableItems.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusIdx(
          (prev) =>
            (prev - 1 + actionableItems.length) % actionableItems.length,
        );
        break;
      case 'Home':
        e.preventDefault();
        setFocusIdx(0);
        break;
      case 'End':
        e.preventDefault();
        setFocusIdx(actionableItems.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusIdx >= 0 && focusIdx < actionableItems.length) {
          const item = actionableItems[focusIdx];
          if (item) onSelect(item.value);
          close();
        }
        break;
    }
  };

  // Build accessible trigger: inject ARIA attrs directly onto the trigger
  // element if it's a valid React element (avoids nested-interactive violation).
  // Falls back to a wrapper <button> for plain text/fragments.
  const triggerProps = {
    'aria-haspopup': 'menu' as const,
    'aria-expanded': isOpen,
    onClick: () => setIsOpen((v: boolean) => !v),
    onKeyDown: handleKeyDown,
  };

  const accessibleTrigger = isValidElement(trigger)
    ? cloneElement(trigger as ReactElement<Record<string, unknown>>, triggerProps)
    : (
        <button
          type="button"
          {...triggerProps}
          className="cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus rounded"
        >
          {trigger}
        </button>
      );

  const menuPortal = isOpen && menuPos
    ? createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{
            position: 'fixed',
            top: menuPos.top,
            left: menuPos.left,
            right: menuPos.right,
          }}
          className={cn(
            'z-[var(--z-dropdown)] min-w-[180px]',
            'bg-bg-elevated border border-border-subtle rounded-lg shadow-lg',
            'py-1 animate-[fade-in-up_150ms_ease-out]',
          )}
          onKeyDown={handleKeyDown}
        >
          {items.map((entry, idx) => {
            if (isDivider(entry)) {
              return (
                <div
                  key={`divider-${idx}`}
                  className="h-px bg-border-subtle my-1"
                  role="separator"
                />
              );
            }

            return (
              <button
                key={entry.value}
                role="menuitem"
                type="button"
                disabled={entry.disabled}
                onClick={() => {
                  onSelect(entry.value);
                  close();
                }}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 text-sm text-left min-h-[var(--touch-target-min)] lg:min-h-0',
                  'transition-colors cursor-pointer',
                  'focus-visible:bg-bg-hover focus:outline-none',
                  'hover:bg-bg-hover',
                  entry.destructive
                    ? 'text-error-text hover:bg-error-bg focus:bg-error-bg'
                    : 'text-text-primary',
                  entry.disabled && 'opacity-50 cursor-not-allowed',
                )}
              >
                {entry.icon && (
                  <span className="shrink-0 w-5">{entry.icon}</span>
                )}
                {entry.label}
              </button>
            );
          })}
        </div>,
        document.body,
      )
    : null;

  return (
    <div ref={containerRef} className={cn('relative inline-flex', className)}>
      {accessibleTrigger}
      {menuPortal}
    </div>
  );
}
